import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import AddMyAddress from "../../UserProfileOverview/MyAddress/AddMyAddress/AddMyAddress";
import { getImageUrl } from "../../../../lib/get-image";
import styles from "./checkoutpayment.module.css";
import PaymentSection from "./PaymentSection";

import { useAddOrderMutation } from "../../../../queries/useOrderQuery";
import { useRemoveCartItem } from "../../../../queries/useCartQuery";
import { paymentOptions } from "../../../../constants/payment-options";
import { useUserAddressListQuery } from "../../../../queries/useUserAddress";

import {
  CART,
  USER_ORDERS,
} from "../../../../constants/query.constants";

import {
  payWithRazorpay,
  razorpayPaymentFailed,
  verifyRazorpayPayment,
} from "../../../../repositories/orders/orderRepository";

import CheckoutStepper from "../../Cart/Checkoutstepper";

const CheckoutPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const {
    data: addressList = [],
    isPending: isPendingAddress,
  } = useUserAddressListQuery();

  const [prescriptions, setPrescriptions] = useState({});
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [storedAddress, setStoredAddress] = useState(null);
  const [savedShipping, setSavedShipping] = useState(null);
  const [showAddressEdit, setShowAddressEdit] = useState(false);

  // =====================================================
  // SAFE DISPLAY VALUE
  // =====================================================

  const getDisplayValue = (value) => {
    if (value == null) return "";

    if (typeof value === "object") {
      return (
        value.name ||
        value.title ||
        value.label ||
        value.value ||
        ""
      );
    }

    return String(value);
  };

  // =====================================================
  // LOAD PRESCRIPTIONS FROM LOCAL STORAGE
  // =====================================================

 

  // =====================================================
  // LOAD CHECKOUT DATA
  // =====================================================

  useEffect(() => {
    if (isPendingAddress) {
      return;
    }

    try {
      const storedAddressData =
        localStorage.getItem("checkoutAddress");

      const storedShippingData =
        localStorage.getItem("shipping-rules");

      const address = storedAddressData
        ? JSON.parse(storedAddressData)
        : null;

      const cartShipping = storedShippingData
        ? JSON.parse(storedShippingData)
        : null;

      // =====================================================
      // ADDRESS VALIDATION
      // =====================================================

      if (!address) {
        localStorage.removeItem(
          "checkoutAddress"
        );

        setStoredAddress(null);

        toast.error(
          "Please select a delivery address"
        );

        navigate("/cart");

        return;
      }

      if (!address.id) {
        localStorage.removeItem(
          "checkoutAddress"
        );

        setStoredAddress(null);

        toast.error(
          "Please select a valid delivery address"
        );

        navigate("/cart");

        return;
      }

      // =====================================================
      // CHECK ADDRESS AGAINST CURRENT ADDRESS LIST
      // =====================================================

      const validAddress = addressList.find(
        (item) =>
          String(item.id) ===
          String(address.id)
      );

      if (!validAddress) {
        localStorage.removeItem(
          "checkoutAddress"
        );

        setStoredAddress(null);

        toast.error(
          "Selected address is no longer available"
        );

        navigate("/cart");

        return;
      }

      setStoredAddress(validAddress);

      // =====================================================
      // NORMAL CART CHECKOUT SHIPPING
      // =====================================================

      if (!location.state?.buyNow) {
        if (!cartShipping) {
          toast.error(
            "Shipping data missing"
          );

          navigate("/cart");

          return;
        }

        setSavedShipping(cartShipping);

        return;
      }

      // =====================================================
      // BUY NOW SHIPPING
      // =====================================================

      const buyNowProduct =
        location.state?.product;

      const buyNowQuantity = Number(
        location.state?.quantity || 1
      );

      if (!buyNowProduct) {
        toast.error(
          "Buy Now product data missing"
        );

        navigate("/cart");

        return;
      }

      const productPrice =
        Number(
          buyNowProduct?.offer_price
        ) > 0
          ? Number(
              buyNowProduct.offer_price
            )
          : Number(
              buyNowProduct?.price || 0
            );

      const buyNowSubtotal =
        productPrice * buyNowQuantity;

      const buyNowShipping =
        buyNowSubtotal >= 1000
          ? {
              name: "Express Delivery",
              description: "Free delivery",
              cost: 0,
            }
          : {
              name: "Standard Delivery",
              description:
                "3–5 business days",
              cost: 100,
            };

      console.log(
        "BUY NOW SUBTOTAL:",
        buyNowSubtotal
      );

      console.log(
        "BUY NOW SHIPPING:",
        buyNowShipping
      );

      setSavedShipping(
        buyNowShipping
      );
    } catch (error) {
      console.error(
        "Checkout data error:",
        error
      );

      localStorage.removeItem(
        "checkoutAddress"
      );

      setStoredAddress(null);

      toast.error(
        "Invalid checkout data"
      );

      navigate("/cart");
    }
  }, [
    addressList,
    isPendingAddress,
    navigate,
    location.state,
  ]);

  // =====================================================
  // EDIT DELIVERY ADDRESS
  // =====================================================

  const handleEditAddress = () => {
    setShowAddressEdit(true);
  };

  const handleCloseAddressEdit = () => {
    setShowAddressEdit(false);
  };

  const handleAddressUpdate = (
    response
  ) => {
    console.log(
      "UPDATED ADDRESS:",
      response
    );

    const updatedAddress =
      response?.data || response;

    if (!updatedAddress?.id) {
      toast.error(
        "Address updated but data not found"
      );

      return;
    }

    setStoredAddress(
      updatedAddress
    );

    localStorage.setItem(
      "checkoutAddress",
      JSON.stringify(updatedAddress)
    );

    setShowAddressEdit(false);
  };

  // =====================================================
  // PRESCRIPTION UPLOAD
  // =====================================================

  const handlePrescriptionUpload = (
    productId,
    event
  ) => {
    const files = Array.from(
      event.target.files || []
    );

    if (!files.length) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];

    const maxSize =
      5 * 1024 * 1024;

    const validFiles =
      files.filter((file) => {
        if (
          !allowedTypes.includes(
            file.type
          )
        ) {
          toast.error(
            `${file.name} is not a valid file. Please upload JPG, PNG or PDF.`
          );

          return false;
        }

        if (file.size > maxSize) {
          toast.error(
            `${file.name} exceeds the 5 MB limit.`
          );

          return false;
        }

        return true;
      });

    if (!validFiles.length) return;

    setPrescriptions(
      (prev) => ({
        ...prev,

        [productId]: [
          ...(prev[productId] || []),

          ...validFiles.map(
            (file) => ({
              file,
              name: file.name,
              type: file.type,
            })
          ),
        ],
      })
    );

    event.target.value = "";
  };

  // =====================================================
  // REMOVE PRESCRIPTION
  // =====================================================

  const removePrescription = (
    productId,
    fileIndex
  ) => {
    setPrescriptions(
      (prev) => {
        const updatedFiles = [
          ...(prev[productId] || []),
        ];

        updatedFiles.splice(
          fileIndex,
          1
        );

        return {
          ...prev,
          [productId]:
            updatedFiles,
        };
      }
    );
  };

  // =====================================================
  // BUY NOW ITEM
  // =====================================================

  const buyNowItem =
    location.state?.buyNow
      ? [
          {
            id:
              location.state.product
                .id,

            quantity:
              location.state.quantity,

            product:
              location.state.product,
          },
        ]
      : null;

  // =====================================================
  // SELECTED CART ITEMS
  // =====================================================

  let selectedCartItems = [];

  try {
    selectedCartItems =
      buyNowItem
        ? buyNowItem
        : JSON.parse(
            localStorage.getItem(
              "selectedCheckoutItems"
            )
          ) || [];
  } catch (error) {
    console.error(
      "Selected checkout items parsing error:",
      error
    );

    selectedCartItems = [];
  }

  const selectedCount =
    selectedCartItems.length;

  // =====================================================
  // SUBTOTAL
  // =====================================================

  const subtotal =
    selectedCartItems.reduce(
      (acc, item) => {
        const price = Number(
          item.product
            ?.offer_price > 0
            ? item.product
                .offer_price
            : item.product
                ?.price ||
              item.price ||
              item.unit_price ||
              0
        );

        return (
          acc +
          price *
            Number(
              item.quantity || 1
            )
        );
      },
      0
    );

  // =====================================================
  // TOTAL QUANTITY
  // =====================================================

  const totalQty =
    selectedCartItems.reduce(
      (acc, item) =>
        acc +
        Number(
          item.quantity ||
            item.qty ||
            1
        ),
      0
    );

  // =====================================================
  // SHIPPING
  // =====================================================

  const shipping = Number(
    typeof savedShipping?.cost ===
      "object"
      ? savedShipping?.cost
          ?.amount ||
          savedShipping?.cost
            ?.value ||
          0
      : savedShipping?.cost ||
          0
  );

  const tax = 0;

  const total =
    subtotal + shipping;

  // =====================================================
  // MUTATIONS
  // =====================================================

  const {
    mutateAsync: placeOrder,
    isPending:
      isOrderPlacePending,
  } = useAddOrderMutation();

  const {
    mutateAsync: removeCartItem,
  } = useRemoveCartItem();

  // =====================================================
  // CLEAR CART
  // =====================================================

  const clearCart = async () => {
    if (buyNowItem) return;

    for (const item of selectedCartItems) {
      if (item?.id) {
        await removeCartItem(
          item.id
        );
      }
    }

    await queryClient.invalidateQueries(
      {
        queryKey: [CART],
      }
    );

    await queryClient.invalidateQueries(
      {
        queryKey: [USER_ORDERS],
      }
    );
  };

  // =====================================================
  // PLACE ORDER
  // =====================================================

  const handlePlaceOrder =
    async () => {
      if (!selectedPayment) {
        toast.error(
          "Select payment method!"
        );

        return;
      }

      try {
        // =================================================
        // BASIC PAYLOAD FOR RAZORPAY
        // =================================================

        const payload = {
          sub_total:
            Number(subtotal),

          amount:
            Number(total),

          product_qty:
            Number(totalQty),

          order_address:
            storedAddress,

          shipping_method:
            savedShipping,

          products:
            selectedCartItems.map(
              (item) => ({
                product_id:
                  item.product_id ||
                  item.product?.id ||
                  item.id ||
                  null,

                vendor_id:
                  item.vendor_id ||
                  item.product
                    ?.vendor_id ||
                  null,

                product_name:
                  getDisplayValue(
                    item.product_name ||
                      item.product
                        ?.name ||
                      item.name
                  ) || "",

                unit_price:
                  Number(
                    item.price ||
                      item.unit_price ||
                      item.product
                        ?.offer_price ||
                      item.product
                        ?.price ||
                      0
                  ),

                qty:
                  Number(
                    item.quantity ||
                      item.qty ||
                      1
                  ),
              })
            ),
        };

        // =================================================
        // FORMDATA
        // =================================================

        const formData =
          new FormData();

        // =================================================
        // BASIC
        // =================================================

        formData.append(
          "sub_total",
          String(
            Number(
              subtotal || 0
            )
          )
        );

        formData.append(
          "amount",
          String(
            Number(
              total || 0
            )
          )
        );

        formData.append(
          "product_qty",
          String(
            Number(
              totalQty || 0
            )
          )
        );

        // =================================================
        // ORDER ADDRESS
        // =================================================

        Object.entries(
          storedAddress || {}
        ).forEach(
          ([key, value]) => {
            let formattedValue =
              value;

            if (
              typeof value ===
                "object" &&
              value !== null
            ) {
              formattedValue =
                value.id ??
                value.name ??
                "";
            }

            formData.append(
              `order_address[${key}]`,
              String(
                formattedValue ??
                  ""
              )
            );
          }
        );

        // =================================================
        // SHIPPING
        // =================================================

        Object.entries(
          savedShipping || {}
        ).forEach(
          ([key, value]) => {
            let formattedValue =
              value;

            if (
              typeof value ===
                "object" &&
              value !== null
            ) {
              formattedValue =
                value.id ??
                value.name ??
                value.amount ??
                "";
            }

            formData.append(
              `shipping_method[${key}]`,
              String(
                formattedValue ??
                  ""
              )
            );
          }
        );

        // =================================================
        // PRODUCTS
        // =================================================

        selectedCartItems.forEach(
          (item, index) => {
            const product =
              item.product || {};

            const productId =
              item.product_id ||
              product.id ||
              item.id ||
              "";

            const vendorId =
              item.vendor_id ||
              product.vendor_id ||
              "";

            const productName =
              getDisplayValue(
                item.product_name ||
                  product.name ||
                  item.name
              ) || "Product";

            const unitPrice =
              Number(
                item.price ||
                  item.unit_price ||
                  product.offer_price ||
                  product.price ||
                  0
              );

            const quantity =
              Number(
                item.quantity ||
                  item.qty ||
                  1
              );

            console.log(
              "🛒 ITEM:",
              item
            );

            console.log(
              "✅ PRODUCT ID:",
              productId
            );

            console.log(
              "✅ VENDOR ID:",
              vendorId
            );

            if (!vendorId) {
              throw new Error(
                `Vendor ID missing for product: ${productName}`
              );
            }

            formData.append(
              `products[${index}][product_id]`,
              String(productId)
            );

            formData.append(
              `products[${index}][vendor_id]`,
              String(vendorId)
            );

            formData.append(
              `products[${index}][product_name]`,
              productName
            );

            formData.append(
              `products[${index}][unit_price]`,
              String(unitPrice)
            );

            formData.append(
              `products[${index}][qty]`,
              String(quantity)
            );
          }
        );

        // =================================================
        // PRESCRIPTION FILES
        // =================================================

        Object.entries(
          prescriptions
        ).forEach(
          ([productId, files]) => {
            files.forEach(
              (fileObj, index) => {
                if (
                  fileObj?.file
                ) {
                  formData.append(
                    `prescriptions[${productId}][${index}]`,
                    fileObj.file
                  );
                }
              }
            );
          }
        );

        // =================================================
        // RAZORPAY
        // =================================================

        if (
          selectedPayment ===
          "razorpay"
        ) {
          const res =
            await payWithRazorpay(
              payload
            );

          if (
            !res?.order ||
            !res?.key
          ) {
            toast.error(
              "Razorpay order creation failed"
            );

            return;
          }

          const {
            order,
            key,
          } = res;

          const options = {
            key,

            amount:
              order.amount,

            currency:
              order.currency,

            name:
              "Your Store",

            description:
              "Order Payment",

            order_id:
              order.id,

            handler:
              async (
                paymentResponse
              ) => {
                try {
                  const verifyRes =
                    await verifyRazorpayPayment(
                      {
                        razorpay_payment_id:
                          paymentResponse.razorpay_payment_id,

                        razorpay_order_id:
                          paymentResponse.razorpay_order_id,

                        razorpay_signature:
                          paymentResponse.razorpay_signature,
                      }
                    );

                  console.log(
                    "VERIFY RESPONSE:",
                    verifyRes
                  );

                  await clearCart();

                  toast.success(
                    "Payment successful 🎉"
                  );

                  navigate(
                    "/checkout/order-success",
                    {
                      state: {
                        payment_id:
                          paymentResponse.razorpay_payment_id,

                        payment_method:
                          "Razorpay",

                        orderId:
                          order?.id,

                        invoice_id:
                          order?.invoice_id,
                      },
                    }
                  );
                } catch (err) {
                  console.error(
                    "Payment verification error:",
                    err
                  );

                  toast.error(
                    "Payment verification failed"
                  );
                }
              },

            modal: {
              ondismiss:
                async () => {
                  console.log(
                    "RAZORPAY POPUP CLOSED"
                  );

                  try {
                    const failureRes =
                      await razorpayPaymentFailed(
                        {
                          razorpay_order_id:
                            order.id,

                          reason:
                            "cancelled_by_user",
                        }
                      );

                    console.log(
                      "FAILURE API RESPONSE:",
                      failureRes
                    );
                  } catch (err) {
                    console.error(
                      "FAILURE API ERROR:",
                      err
                    );
                  }

                  toast.error(
                    "Payment cancelled"
                  );
                },
            },

            prefill: {
              name:
                getDisplayValue(
                  storedAddress?.name ||
                    storedAddress?.full_name
                ),

              email:
                getDisplayValue(
                  storedAddress?.email
                ),

              contact:
                getDisplayValue(
                  storedAddress?.phone
                ),
            },

            theme: {
              color:
                "#3399cc",
            },
          };

          if (
            typeof window
              .Razorpay !==
            "function"
          ) {
            toast.error(
              "Razorpay is not loaded. Please refresh and try again."
            );

            return;
          }

          const razor =
            new window.Razorpay(
              options
            );

          razor.on(
            "payment.failed",
            async (
              response
            ) => {
              console.log(
                "PAYMENT FAILED EVENT",
                response
              );

              try {
                const failureRes =
                  await razorpayPaymentFailed(
                    {
                      razorpay_order_id:
                        order.id,

                      razorpay_payment_id:
                        response
                          ?.error
                          ?.metadata
                          ?.payment_id ||
                        null,

                      reason:
                        response
                          ?.error
                          ?.description ||
                        "payment_failed",
                    }
                  );

                console.log(
                  "FAILURE API RESPONSE:",
                  failureRes
                );
              } catch (err) {
                console.error(
                  "FAILURE API ERROR:",
                  err
                );
              }
            }
          );

          razor.open();

          return;
        }

        // =================================================
        // CASH ON DELIVERY
        // =================================================

        if (
          selectedPayment ===
          "cod"
        ) {
          const response =
            await placeOrder(
              formData
            );

          const invoiceId =
            response?.order
              ?.invoice_id;

          const orderId =
            response?.order?.id;

          await clearCart();

          toast.success(
            "COD order placed successfully 🎉"
          );

          navigate(
            "/checkout/success",
            {
              state: {
                invoiceId,
                orderId,
              },
            }
          );

          return;
        }
      } catch (err) {
        console.log(
          "🔥 FULL ERROR:",
          err
        );

        console.log(
          "🔥 STATUS:",
          err?.response?.status
        );

        console.log(
          "🔥 DATA:",
          err?.response?.data
        );

        console.log(
          "🔥 MESSAGE:",
          err?.message
        );

        toast.error(
          err?.response?.data
            ?.message ||
            err?.response?.data
              ?.error ||
            err?.message ||
            "Order failed"
        );
      }
    };

  // =====================================================
  // LOADING
  // =====================================================

  if (
    !storedAddress ||
    !savedShipping
  ) {
    return null;
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <section
      className={styles.wrapper}
    >
      {/* =================================================
          EDIT DELIVERY ADDRESS POPUP
      ================================================= */}

      {showAddressEdit &&
        storedAddress && (
          <div
            className={
              styles.addressEditOverlay
            }
          >
            <div
              className={
                styles.addressEditPopup
              }
            >
              <AddMyAddress
                mode="edit"
                initialData={
                  storedAddress
                }
                addressId={
                  storedAddress?.id
                }
                onClose={
                  handleCloseAddressEdit
                }
                onSave={
                  handleAddressUpdate
                }
              />
            </div>
          </div>
        )}

      {/* =================================================
          CHECKOUT STEPPER
      ================================================= */}

      <div
        className={
          styles.checkoutHeader
        }
      >
        <div
          className={
            styles.bannerContent
          }
        >
          <span
            className={
              styles.bannerSmallText
            }
          >
            CHECKOUT
          </span>

          <h1>Payment</h1>

          <p>
            Review your order and
            choose your preferred
            payment method to
            complete your purchase.
          </p>
        </div>

        <CheckoutStepper
          currentStep={2}
        />
      </div>

      <div
        className={
          styles.contentContainer
        }
      >
        {/* =================================================
            LEFT COLUMN
        ================================================= */}

        <div
          className={
            styles.leftColumn
          }
        >
          {/* =================================================
              PAYMENT METHOD
          ================================================= */}

          <div
            className={
              styles.paymentCard
            }
          >
            <div
              className={
                styles.cardHeader
              }
            >
              <div>
                <span
                  className={
                    styles.sectionNumber
                  }
                ></span>

                <h2>
                  Payment Method
                </h2>
              </div>

              <span
                className={
                  styles.secureBadge
                }
              >
                🔒 Secure
              </span>
            </div>

            <p
              className={
                styles.cardDescription
              }
            >
              Choose your preferred
              payment method to
              complete your order.
            </p>

            <PaymentSection
              paymentOptions={
                paymentOptions
              }
              selectedPayment={
                selectedPayment
              }
              setSelectedPayment={
                setSelectedPayment
              }
            />
          </div>

          {/* =================================================
              ORDER REVIEW
          ================================================= */}

          <div
            className={
              styles.reviewCard
            }
          >
            <div
              className={
                styles.cardHeader
              }
            >
              <div>
                <span
                  className={
                    styles.sectionNumber
                  }
                ></span>

                <h2>
                  Order Review
                </h2>
              </div>
            </div>

            {/* =================================================
                DELIVERY ADDRESS
            ================================================= */}

            <div
              className={
                styles.reviewBlock
              }
            >
              <div
                className={
                  styles.reviewBlockHeader
                }
              >
                <h3>
                  Deliver to
                </h3>

                <button
                  type="button"
                  className={
                    styles.smallEditButton
                  }
                  onClick={
                    handleEditAddress
                  }
                >
                  Edit
                </button>
              </div>

              <div
                className={
                  styles.addressReview
                }
              >
                <strong>
                  {getDisplayValue(
                    storedAddress?.type ||
                      storedAddress?.address_type
                  ) ||
                    "Delivery Address"}
                </strong>

                <p>
                  {getDisplayValue(
                    storedAddress?.name ||
                      storedAddress?.full_name
                  )}
                </p>

                <p>
                  {getDisplayValue(
                    storedAddress?.address ||
                      storedAddress?.address_line1
                  )}
                </p>

                <p>
                  {getDisplayValue(
                    storedAddress?.city
                  )}

                  {storedAddress?.city &&
                  storedAddress?.state
                    ? ", "
                    : ""}

                  {getDisplayValue(
                    storedAddress?.state
                  )}
                </p>

                <p>
                  {getDisplayValue(
                    storedAddress?.pincode ||
                      storedAddress?.postal_code
                  )}
                </p>

                {storedAddress?.phone && (
                  <p>
                    Phone:{" "}
                    {getDisplayValue(
                      storedAddress.phone
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* =================================================
                SHIPPING METHOD
            ================================================= */}
{/* =================================================
    PRESCRIPTION
================================================= */}


{/* =================================================
    PRESCRIPTION
================================================= */}

{selectedCartItems.some((item) => {
  const product = item.product || {};

  return (
    Number(
      item.is_prescription ??
        product.is_prescription ??
        0
    ) === 1
  );
}) && (
  <div className={styles.prescriptionBlock}>

    <div className={styles.prescriptionHeader}>
      <div>
        <h3>Prescription</h3>

        <span>
          Upload prescription for applicable medicines
        </span>
      </div>

      <span className={styles.prescriptionRequiredBadge}>
        Required
      </span>
    </div>

    <div className={styles.prescriptionProducts}>

      <div className={styles.prescriptionProductRow}>

        {/* UPLOAD SECTION */}
        <div className={styles.prescriptionActions}>

          <label
            htmlFor="prescription-upload"
            className={styles.compactUploadButton}
          >
            <span>📎</span>

            {Object.values(prescriptions).flat().length > 0
              ? "Add More"
              : "Upload"}
          </label>

          <input
            id="prescription-upload"
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            multiple
            className={styles.prescriptionInput}
            onChange={(event) => {
              handlePrescriptionUpload(
                "common",
                event
              );
            }}
          />

          {/* UPLOADED FILES */}
          {prescriptions.common?.length > 0 && (
            <div className={styles.compactUploadedFiles}>

              {prescriptions.common.map(
                (fileObj, fileIndex) => {

                  const fileName =
                    fileObj?.file?.name ||
                    fileObj?.name ||
                    "Prescription";

                  return (
                    <div
                      className={styles.compactFile}
                      key={`common-file-${fileIndex}`}
                      title={fileName}
                    >
                      <span
                        className={
                          styles.compactFileIcon
                        }
                      >
                        📄
                      </span>

                      <span
                        className={
                          styles.compactFileName
                        }
                      >
                        {fileName}
                      </span>

                      <button
                        type="button"
                        className={
                          styles.compactRemoveButton
                        }
                        onClick={() =>
                          removePrescription(
                            "common",
                            fileIndex
                          )
                        }
                        aria-label="Remove prescription"
                      >
                        ×
                      </button>
                    </div>
                  );
                }
              )}

            </div>
          )}

        </div>

      </div>

    </div>

    <div className={styles.prescriptionHintBottom}>
      JPG, PNG or PDF · Maximum 5 MB per file
    </div>

  </div>
)}

            <div
              className={
                styles.reviewBlock
              }
            >
              <div
                className={
                  styles.reviewBlockHeader
                }
              >
                <h3>
                  Shipping Method
                </h3>
              </div>

              <div
                className={
                  styles.shippingReview
                }
              >
                <div
                  className={
                    styles.shippingIcon
                  }
                >
                  🚚
                </div>

                <div
                  className={
                    styles.shippingDetails
                  }
                >
                  <strong>
                    {getDisplayValue(
                      savedShipping?.name ||
                        savedShipping?.title ||
                        savedShipping?.shipping_name
                    ) ||
                      "Standard Delivery"}
                  </strong>

                  <span>
                    {getDisplayValue(
                      savedShipping?.description ||
                        savedShipping?.delivery_time
                    ) ||
                      "3–5 business days"}
                  </span>
                </div>

                <strong
                  className={
                    styles.shippingPrice
                  }
                >
                  {shipping === 0
                    ? "FREE"
                    : `₹${shipping}`}
                </strong>
              </div>
            </div>

            {/* =================================================
                ITEMS
            ================================================= */}

            <div
              className={
                styles.reviewBlock
              }
            >
              <div
                className={
                  styles.reviewItems
                }
              >
                {selectedCartItems.map(
                  (item, index) => {
                    const product =
                      item.product || {};

                    // =========================================
                    // PRODUCT ID
                    // =========================================

                    const productId =
                      item.product_id ||
                      product.id ||
                      item.id ||
                      "";

                    // =========================================
                    // PRESCRIPTION REQUIRED
                    // =========================================

                  

                    const productName =
                      getDisplayValue(
                        item.product_name ||
                          product.name ||
                          item.name
                      ) ||
                      "Product";

                    const image =
                      product.thumb_image ||
                      product.image ||
                      product.thumbnail ||
                      product.product_image ||
                      item.image ||
                      "";

                    const price =
                      Number(
                        product.offer_price >
                          0
                          ? product.offer_price
                          : product.price ||
                            item.price ||
                            item.unit_price ||
                            0
                      );

                    const quantity =
                      Number(
                        item.quantity ||
                          item.qty ||
                          1
                      );

                    return (
                      <div
                        className={
                          styles.reviewItem
                        }
                        key={
                          item.id ||
                          product.id ||
                          index
                        }
                      >
                        {/* IMAGE */}

                        <div
                          className={
                            styles.reviewItemImage
                          }
                        >
                          {image ? (
                            <img
                              src={getImageUrl(
                                image
                              )}
                              alt={
                                productName
                              }
                            />
                          ) : (
                            <div
                              className={
                                styles.noImage
                              }
                            >
                              🛍️
                            </div>
                          )}
                        </div>

                        {/* INFO */}

                        <div
                          className={
                            styles.reviewItemInfo
                          }
                        >
                          <strong>
                            {
                              productName
                            }
                          </strong>

                          <span>
                            Qty:{" "}
                            {
                              quantity
                            }
                          </span>

                          {/* =================================================
                              PRESCRIPTION UPLOAD
                          ================================================= */}

  
                        </div>

                        {/* PRICE */}

                        <strong
                          className={
                            styles.reviewItemPrice
                          }
                        >
                          ₹
                          {(
                            price *
                            quantity
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </strong>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>

        {/* =================================================
            RIGHT COLUMN - ORDER SUMMARY
        ================================================= */}

        <aside
          className={
            styles.orderSummarySection
          }
        >
          <div
            className={
              styles.summaryHeader
            }
          >
            <div>
              <h2>
                Order Summary
              </h2>

              <span>
                {selectedCount}{" "}
                product
                {selectedCount !==
                1
                  ? "s"
                  : ""}
              </span>
            </div>
          </div>

          {/* =================================================
              PRICE DETAILS
          ================================================= */}

          <div
            className={
              styles.priceDetails
            }
          >
            <div
              className={
                styles.summaryRow
              }
            >
              <span>
                Subtotal (
                {totalQty}{" "}
                item
                {totalQty > 1
                  ? "s"
                  : ""}
                )
              </span>

              <strong>
                ₹
                {subtotal.toLocaleString(
                  "en-IN"
                )}
              </strong>
            </div>

            <div
              className={
                styles.summaryRow
              }
            >
              <span>
                Shipping
              </span>

              <strong
                className={
                  styles.freeShipping
                }
              >
                {shipping === 0
                  ? "FREE"
                  : `₹${shipping.toLocaleString(
                      "en-IN"
                    )}`}
              </strong>
            </div>

            <div
              className={
                styles.summaryRow
              }
            >
              <span>
                Tax
              </span>

              <strong>
                ₹
                {tax.toLocaleString(
                  "en-IN"
                )}
              </strong>
            </div>
          </div>

          {/* =================================================
              TOTAL
          ================================================= */}

          <div
            className={
              styles.summaryTotal
            }
          >
            <span>
              Total Amount
            </span>

            <strong>
              ₹
              {total.toLocaleString(
                "en-IN"
              )}
            </strong>
          </div>

          {/* =================================================
              SAVINGS
          ================================================= */}

          {subtotal > total && (
            <div
              className={
                styles.savingsMessage
              }
            >
              🎉 You are saving ₹
              {(
                subtotal - total
              ).toLocaleString(
                "en-IN"
              )}
            </div>
          )}

          {/* =================================================
              PLACE ORDER
          ================================================= */}

          <button
            type="button"
            className={
              styles.payButton
            }
            onClick={
              handlePlaceOrder
            }
            disabled={
              isOrderPlacePending ||
              !selectedPayment
            }
          >
            {isOrderPlacePending ? (
              <>
                <span
                  className={
                    styles.btn_spinner
                  }
                ></span>

                Processing...
              </>
            ) : (
              <>
                Place Order

                <span>
                  →
                </span>
              </>
            )}
          </button>

          <p
            className={
              styles.paymentTerms
            }
          >
            By placing your
            order, you agree to
            our{" "}
            <button
              type="button"
              className={
                styles.termsLink
              }
              onClick={() =>
                navigate(
                  "/terms-and-conditions"
                )
              }
            >
              Terms & Conditions
            </button>{" "}
            and{" "}
            <button
              type="button"
              className={
                styles.termsLink
              }
              onClick={() =>
                navigate(
                  "/privacy-policy"
                )
              }
            >
              Privacy Policy
            </button>
            .
          </p>

          {/* =================================================
              SECURITY
          ================================================= */}

          <div
            className={
              styles.securityBox
            }
          >
            <div
              className={
                styles.securityIcon
              }
            >
              🛡️
            </div>

            <div>
              <strong>
                Safe & Secure
                Payments
              </strong>

              <p>
                Your payment
                information is
                protected
              </p>
            </div>
          </div>

          {/* =================================================
              TRUST
          ================================================= */}

          <div
            className={
              styles.trustList
            }
          >
            <div
              className={
                styles.trustItem
              }
            >
              <span>↩</span>

              <div>
                <strong>
                  Easy Returns
                </strong>

                <p>
                  7 days return
                  policy
                </p>
              </div>
            </div>

            <div
              className={
                styles.trustItem
              }
            >
              <span>◉</span>

              <div>
                <strong>
                  24/7 Support
                </strong>

                <p>
                  We are here to
                  help
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default CheckoutPayment;